import { useState, useEffect } from 'react';
import { X, MessageCircle } from 'lucide-react';

export function FloatingWhatsAppButton() {
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);

  useEffect(() => {
    // Show button after loading screen completes (5 seconds)
    const showTimer = setTimeout(() => setIsVisible(true), 5000);
    
    // Auto-expand ONLY on desktop after 7 seconds if user hasn't interacted
    const expandTimer = setTimeout(() => {
      if (!hasInteracted && window.innerWidth >= 768) {
        setIsExpanded(true);
        // Auto-collapse after 6 seconds
        setTimeout(() => setIsExpanded(false), 6000);
      }
    }, 7000);

    return () => {
      clearTimeout(showTimer);
      clearTimeout(expandTimer);
    };
  }, [hasInteracted]);

  const handleClick = () => {
    setHasInteracted(true);
    const greeting = 'Hi%20Janet!%20I%27m%20interested%20in%20the%20February%202026%20Digital%20Marketing%20Training.%20Can%20you%20share%20more%20details%3F';
    window.open(`https://wa.me/254704544323?text=${greeting}`, '_blank');
  };

  const handleToggleExpand = () => {
    setHasInteracted(true);
    setIsExpanded(!isExpanded);
  };

  const handleMinimize = () => {
    setIsMinimized(true);
    setIsExpanded(false);
    // Auto-restore after 30 seconds
    setTimeout(() => setIsMinimized(false), 30000);
  };

  if (!isVisible) return null;

  // Minimized state - tiny badge in corner
  if (isMinimized) {
    return (
      <button
        onClick={() => setIsMinimized(false)}
        className="fixed bottom-4 right-4 w-12 h-12 bg-green-500 hover:bg-green-600 rounded-full shadow-lg flex items-center justify-center z-50 transition-all duration-300 hover:scale-110"
        aria-label="Show WhatsApp chat"
      >
        <MessageCircle className="w-6 h-6 text-white" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 md:bottom-6 md:right-6 z-40 flex flex-col items-end gap-3 animate-slide-in-right">
      {/* Expanded Message Card - Only shows when clicked or hovered on desktop */}
      {isExpanded && (
        <div className="relative animate-scale-in">
          <button
            onClick={() => setIsExpanded(false)}
            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-colors shadow-lg z-10"
            aria-label="Close message"
          >
            <X className="w-3 h-3" />
          </button>
          
          <div className="bg-white rounded-2xl shadow-2xl p-4 w-64 md:w-72 border-2 border-green-100 relative overflow-hidden">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center flex-shrink-0 shadow-md">
                <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
              </div>
              
              <div className="flex-1 min-w-0">
                <p className="font-bold text-gray-900 text-sm mb-1">
                  Janet Marketing Academy
                </p>
                <p className="text-gray-700 text-xs leading-relaxed mb-3">
                  Questions about our February 2026 intake?
                  <br />
                  <span className="font-semibold text-green-600">Limited spots available!</span>
                </p>
                <button
                  onClick={handleClick}
                  className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-4 py-2 rounded-lg text-xs font-semibold transition-all duration-300 shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                  Chat on WhatsApp
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main WhatsApp Button - Smaller on mobile, less aggressive */}
      <div className="relative group">
        {/* Minimize button - only visible when expanded */}
        {isExpanded && (
          <button
            onClick={handleMinimize}
            className="absolute -top-10 right-0 bg-gray-800/80 hover:bg-gray-900 text-white text-xs px-3 py-1 rounded-full shadow-lg transition-all duration-300 z-10"
            aria-label="Minimize chat"
          >
            Hide for now
          </button>
        )}

        <button
          onClick={handleToggleExpand}
          onMouseEnter={() => !hasInteracted && window.innerWidth >= 768 && setIsExpanded(true)}
          onMouseLeave={() => window.innerWidth >= 768 && setTimeout(() => setIsExpanded(false), 2000)}
          className="relative group"
          aria-label="Chat on WhatsApp"
        >
          {/* Subtle pulse rings - less aggressive on mobile */}
          <div className="absolute -inset-2 bg-green-500/20 rounded-full animate-ping-subtle hidden md:block"></div>
          
          {/* Notification dot - smaller on mobile */}
          <div className="absolute -top-1 -right-1 w-2.5 h-2.5 md:w-3 md:h-3 bg-red-500 rounded-full border-2 border-white animate-pulse z-10"></div>
          
          {/* Main button - smaller on mobile */}
          <div className="relative w-12 h-12 md:w-14 md:h-14 bg-gradient-to-br from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 rounded-full shadow-lg md:shadow-2xl flex items-center justify-center transition-all duration-300 group-hover:scale-110 cursor-pointer border-2 border-white">
            <svg className="w-6 h-6 md:w-7 md:h-7 text-white group-hover:scale-110 transition-transform" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
            </svg>
          </div>
        </button>
      </div>

      <style>{`
        @keyframes slide-in-right {
          from {
            opacity: 0;
            transform: translateX(100px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes scale-in {
          from {
            opacity: 0;
            transform: scale(0.8) translateY(10px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }

        @keyframes ping-subtle {
          0% {
            transform: scale(1);
            opacity: 0.3;
          }
          75%, 100% {
            transform: scale(1.3);
            opacity: 0;
          }
        }

        .animate-slide-in-right {
          animation: slide-in-right 0.6s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .animate-scale-in {
          animation: scale-in 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .animate-ping-subtle {
          animation: ping-subtle 3s cubic-bezier(0, 0, 0.2, 1) infinite;
        }
      `}</style>
    </div>
  );
}